import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Utility functions for sync operations
export async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Calculate exponential backoff with jitter
export function calculateBackoff(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  jitterFactor: number
): number {
  const multiplier = Math.pow(2, attempt);
  const jitter = (1 - jitterFactor) + (Math.random() * (jitterFactor * 2));
  const delay = baseDelayMs * multiplier * jitter;
  return Math.min(delay, maxDelayMs);
}

// Run async operations with concurrency control
export async function runWithConcurrency<T, R>(
  items: T[],
  asyncFn: (item: T) => Promise<R>,
  limit: number
): Promise<R[]> {
  const results: R[] = [];
  const executing: Set<Promise<void>> = new Set();
  
  for (const item of items) {
    const promise = asyncFn(item).then(result => {
      results.push(result);
    }).finally(() => {
      executing.delete(promise);
    });
    
    executing.add(promise);
    
    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }
  
  await Promise.all(executing);
  return results;
}
